import React, { FC } from "react";
import { get } from "lodash";

import { ArrayInput } from "./ArrayInput";
import { BooleanInput } from "./BooleanInput";
import { DeleteArrayItemButton } from "./DeleteArrayItemButton";
import { DocumentationButton } from "./DocumentationButton";
import { getSchema } from "./GetSchema";
import { JsonSchemaObject } from "./JsonSchemaObject";
import { NumberInput } from "./NumberInput";
import { TextInput } from "./TextInput";
import { IInputProps, JsonSchema, ServiceParameters } from "./types";

export const JsonSchemaFields: FC<IInputProps> = props => {
    const { params, path, setParams, currentPath, setCurrentPath, validation } =
        props;

    const schema = getSchema(props.schema, currentPath);

    switch (schema.type) {
        case "string": {
            return (
                <TextInput
                    params={params}
                    path={path}
                    schema={schema}
                    setParams={setParams}
                    currentPath={currentPath}
                    setCurrentPath={setCurrentPath}
                    validation={validation}
                />
            );
        }
        case "number": {
            return (
                <NumberInput
                    params={params}
                    path={path}
                    schema={schema}
                    setParams={setParams}
                    currentPath={currentPath}
                    setCurrentPath={setCurrentPath}
                    validation={validation}
                />
            );
        }
        case "boolean": {
            return (
                <BooleanInput
                    params={params}
                    path={path}
                    schema={schema}
                    setParams={setParams}
                    currentPath={currentPath}
                    setCurrentPath={setCurrentPath}
                    validation={validation}
                />
            );
        }
        case "integer": {
            return (
                <NumberInput
                    integerOnly={true}
                    params={params}
                    path={path}
                    schema={schema}
                    setParams={setParams}
                    currentPath={currentPath}
                    setCurrentPath={setCurrentPath}
                    validation={validation}
                />
            );
        }
        case "object": {
            const properties = schema?.properties;
            if (!properties) {
                return null;
            }

            const documentationUrl = schema["$formant.documentationUrl"];

            /* if the object just has one key, just show the children
            if (Object.keys(properties).length === 1 && !documentationUrl) {
                const key = Object.keys(properties)[0];
                const childSchema = properties[key];
                const nextPath = [...currentPath, key];
                return (
                    <JsonSchemaFields
                        path={nextPath}
                        params={params}
                        setParams={setParams}
                        currentPath={nextPath}
                        setCurrentPath={setCurrentPath}
                        schema={childSchema}
                        validation={validation}
                    />
                );
             */

            return (
                <>
                    <JsonSchemaObject
                        path={path}
                        params={params}
                        setParams={setParams}
                        currentPath={currentPath}
                        setCurrentPath={setCurrentPath}
                        properties={properties}
                        schema={schema}
                        validation={validation}
                    />
                    {documentationUrl && (
                        <DocumentationButton url={documentationUrl} />
                    )}
                </>
            );
        }
        case "array": {
            const { items } = schema;
            const { properties } = items;

            if (items.type !== "object") {
                return (
                    <ArrayInput
                        params={params}
                        path={path}
                        schema={schema}
                        setParams={setParams}
                        currentPath={currentPath}
                        setCurrentPath={setCurrentPath}
                        validation={validation}
                    />
                );
            }

            return (
                <>
                    <JsonSchemaObject
                        properties={properties}
                        path={path}
                        params={params}
                        setParams={setParams}
                        currentPath={currentPath}
                        setCurrentPath={setCurrentPath}
                        schema={schema}
                        validation={validation}
                    />
                    <DeleteArrayItemButton
                        path={path}
                        params={params}
                        setParams={setParams}
                        currentPath={currentPath}
                        setCurrentPath={setCurrentPath}
                        schema={schema}
                        validation={validation}
                    />
                </>
            );
        }

        default:
            return <>Unsupported schema.type</>;
    }
};

export const getArrayValue = (
    params: ServiceParameters,
    currentPath: string[],
): [] => {
    return get(params, currentPath) || [];
};

export const isNumberString = (value: string): boolean => {
    return isNaN(parseInt(value, 10));
};

export const isPathSkippable = (
    schema: JsonSchema,
    path: string[],
): boolean => {
    const currentSchema = getSchema(schema, path);
    return (
        currentSchema.type === "object" &&
        Object.keys(currentSchema.properties).length === 1
    );
};

export const isPathAnArrayIndex = (currentPath: string[]): boolean => {
    return !isNumberString([...currentPath].pop() || "not a number");
};

export const parseCurrentDisplayPath = (currentPath: string[]): string[] => {
    if (currentPath.length === 0) {
        return currentPath;
    }
    const slice = currentPath.slice(0, currentPath.length - 1);
    if (!isPathAnArrayIndex(slice)) {
        return slice;
    }
    return parseCurrentDisplayPath(slice);
};
